import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { Link } from 'react-router-dom';
import { services, serviceCategories, getServiceCategoryKey } from '@/mocks/services';
import { useState, useMemo } from 'react';

const serviceImages: Record<string, string> = {
  'market-expansion': 'https://readdy.ai/api/search-image?query=Professional%20business%20people%20discussing%20global%20market%20expansion%20strategy%20in%20modern%20office%20with%20world%20map%20background%20warm%20golden%20tones%20corporate&width=800&height=500&seq=svc-market&orientation=landscape',
  'cross-border-cooperation': 'https://readdy.ai/api/search-image?query=Two%20business%20executives%20shaking%20hands%20across%20a%20table%20with%20global%20map%20projection%20elegant%20corporate%20setting%20warm%20amber%20lighting&width=800&height=500&seq=svc-cross&orientation=landscape',
  'resource-integration': 'https://readdy.ai/api/search-image?query=Connected%20network%20nodes%20over%20world%20map%20with%20light%20beams%20connecting%20continents%20modern%20digital%20visualization%20warm%20corporate%20style&width=800&height=500&seq=svc-resource&orientation=landscape',
  'strategy-consulting': 'https://readdy.ai/api/search-image?query=Senior%20consultants%20reviewing%20strategic%20documents%20around%20modern%20conference%20table%20professional%20corporate%20environment%20warm%20tones&width=800&height=500&seq=svc-strategy&orientation=landscape',
  'brand-internationalization': 'https://readdy.ai/api/search-image?query=Creative%20brand%20strategy%20presentation%20on%20large%20screen%20modern%20creative%20agency%20workspace%20warm%20sophisticated%20atmosphere&width=800&height=500&seq=svc-brand&orientation=landscape',
  'digital-globalization': 'https://readdy.ai/api/search-image?query=Digital%20marketing%20dashboard%20with%20global%20analytics%20on%20multiple%20screens%20modern%20tech%20office%20warm%20ambient%20lighting&width=800&height=500&seq=svc-digital&orientation=landscape',
};

export default function ServicesPage() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredServices = useMemo(() => {
    if (activeCategory === 'all') return services;
    return services.filter((s) => getServiceCategoryKey(s.id) === activeCategory);
  }, [activeCategory]);

  return (
    <>
      <SEOHead
        title="服务项目"
        description="征途国际提供全球市场拓展、跨境业务合作、国际资源对接、国际化战略咨询、品牌国际化和数字全球化等六大核心服务，一站式助力企业走向世界。"
        keywords="征途国际服务,全球市场拓展,跨境业务合作,国际资源对接,国际化战略咨询"
        canonicalUrl="https://exportr.github.io/services"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            'name': '征途国际 - 服务项目',
            'description': '征途国际提供全球市场拓展、跨境业务合作、国际资源对接等六大核心服务。',
            'url': 'https://exportr.github.io/services',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://exportr.github.io' },
              { '@type': 'ListItem', 'position': 2, 'name': '服务项目', 'item': 'https://exportr.github.io/services' },
            ],
          },
        ]}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        <section className="bg-background-100/70 py-12 md:py-16">
          <div className="w-full px-6 md:px-10">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '服务项目' }]} />

            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                  我们的服务
                </h1>
                <p className="text-sm text-foreground-600 max-w-xl mx-auto leading-relaxed">
                  六大核心服务板块，全方位助力企业征战全球市场
                </p>
              </div>

              <div className="flex items-center justify-center flex-wrap gap-2 pb-8">
                {serviceCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 border border-background-200/60 hover:border-primary-300/40'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredServices.map((service) => (
                  <div
                    key={service.id}
                    className="group bg-background-50 border border-background-200/60 rounded-lg overflow-hidden hover:border-primary-300/40 transition-all duration-300"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={serviceImages[service.id]}
                        alt={service.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-primary-100/80 rounded-lg">
                          <i className={`${service.icon} text-base text-primary-600`}></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-950 font-heading">{service.title}</h3>
                          <p className="text-xs text-foreground-400">{service.subtitle}</p>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">{service.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {service.features.map((f) => (
                          <span
                            key={f}
                            className="text-xs px-2.5 py-1 bg-secondary-100/80 text-secondary-700 rounded-full"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}